import React from "react";
import {
  Box,
  Button,
  CardContent,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import SaveIcon from "@mui/icons-material/Save";
import LockIcon from "@mui/icons-material/Lock";
import DeleteIcon from "@mui/icons-material/Delete";
import color from "../../components/utils/Colors";
import { TabPanel } from "./Settings";

// ── Validation schema ──────────────────────────────────────────────────────────
const accountSchema = Yup.object({
  username: Yup.string()
    .min(3, "Username must be at least 3 characters")
    .max(30, "Username cannot exceed 30 characters")
    .matches(
      /^[a-zA-Z0-9_]+$/,
      "Username can only contain letters, numbers, and underscores",
    )
    .required("Username is required"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  phone: Yup.string()
    .matches(
      /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/,
      "Please enter a valid phone number",
    )
    .min(7, "Phone number is too short")
    .required("Phone number is required"),
});

// ── Props ──────────────────────────────────────────────────────────────────────
interface AccountTabProps {
  currentTab: number;
  initialValues: {
    email: string;
    username: string;
    phone: string;
  };
  onSave: (values: { email: string; username: string; phone: string }) => void;
  onOpenPasswordDialog: () => void;
  onOpenDeleteDialog: () => void;
}

// ── SettingItem helper (local) ─────────────────────────────────────────────────
const SettingItem = ({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<any>;
  title: string;
  description: string;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      padding: "16px 0",
    }}
  >
    <Icon
      sx={{ color: color.firstColor, marginRight: "16px", fontSize: "24px" }}
    />
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="h6"
        sx={{ color: color.textColor, fontWeight: "bold" }}
      >
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: "#999", marginTop: "4px" }}>
        {description}
      </Typography>
    </Box>
  </Box>
);

// ── Component ──────────────────────────────────────────────────────────────────
const AccountTab: React.FC<AccountTabProps> = ({
  currentTab,
  initialValues,
  onSave,
  onOpenPasswordDialog,
  onOpenDeleteDialog,
}) => {
  return (
    <TabPanel value={currentTab} index={0}>
      <CardContent sx={{ padding: "30px" }}>
        <Typography
          variant="h6"
          sx={{
            color: color.textColor,
            fontWeight: "bold",
            marginBottom: "20px",
          }}
        >
          Account Information
        </Typography>

        <Formik
          initialValues={initialValues}
          validationSchema={accountSchema}
          enableReinitialize
          onSubmit={(values) => onSave(values)}
        >
          {({ isSubmitting }) => (
            <Form>
              <Grid container spacing={3} sx={{ marginBottom: "30px" }}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    component="label"
                    htmlFor="username"
                    variant="body2"
                    sx={{ color: "#666", display: "block", mb: 0.5 }}
                  >
                    Username
                  </Typography>
                  <TextField
                    fullWidth
                    id="username"
                    name="username"
                    variant="outlined"
                    className="input-root2"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    component="label"
                    htmlFor="email"
                    variant="body2"
                    sx={{ color: "#666", display: "block", mb: 0.5 }}
                  >
                    Email Address
                  </Typography>
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    type="email"
                    variant="outlined"
                    className="input-root2"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    component="label"
                    htmlFor="phone"
                    variant="body2"
                    sx={{ color: "#666", display: "block", mb: 0.5 }}
                  >
                    Phone Number
                  </Typography>
                  <TextField
                    fullWidth
                    id="phone"
                    name="phone"
                    variant="outlined"
                    className="input-root2"
                  />
                </Grid>
              </Grid>

              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={isSubmitting}
                sx={{
                  background: color.firstColor,
                  color: "white",
                  textTransform: "none",
                  fontWeight: "bold",
                  marginBottom: "30px",
                  "&:hover": { background: color.forthColor },
                }}
              >
                Save Changes
              </Button>
            </Form>
          )}
        </Formik>

        <Divider sx={{ marginY: "30px" }} />

        {/* Change Password */}
        <Box sx={{ marginBottom: "30px" }}>
          <SettingItem
            icon={LockIcon}
            title="Change Password"
            description="Update your password to keep your account secure"
          />
          <Button
            variant="outlined"
            sx={{
              marginTop: "16px",
              borderColor: color.firstColor,
              color: color.firstColor,
              textTransform: "none",
              fontWeight: "bold",
              "&:hover": {
                borderColor: color.forthColor,
                color: color.forthColor,
              },
            }}
            onClick={onOpenPasswordDialog}
          >
            Change Password
          </Button>
        </Box>

        <Divider sx={{ marginY: "30px" }} />

        {/* Delete Account */}
        <Box>
          <SettingItem
            icon={DeleteIcon}
            title="Delete Account"
            description="Permanently delete your account and all associated data"
          />
          <Button
            variant="outlined"
            color="error"
            sx={{
              marginTop: "16px",
              textTransform: "none",
              fontWeight: "bold",
            }}
            onClick={onOpenDeleteDialog}
          >
            Delete Account
          </Button>
        </Box>
      </CardContent>
    </TabPanel>
  );
};

export default AccountTab;
